import { Button, Col, Row, Input } from "antd";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ReactQuill from "react-quill";
import DOMPurify from "dompurify";
import { debounce } from "lodash";
import beautify from "js-beautify";
import CustomFormItem from "../../components/CustomFormItem/CustomFormItem";
import LabelWithDropDown from "../../components/LabelWithDropDown/LabelWithDropDown";
import { useLanguage } from "../../hooks";
import {
  LANGUAGE_CODES,
  FORM_FIELDS_LOOKUP,
  HELPER_TEXT_KEYS,
  BUILDING_TABS_KEYS,
  BUILDING_REQUIRED_FIELDS_LOOKUP,
  FORM_FIELDS_TYPE,
  USER_ROLES,
} from "../../utils/constants";
import { validateMarketingDescription } from "../../utils/utils";
import AppModal from "../../components/AppModal/AppModal";
import permissionsUtil from "../../utils/permissions.util";
import "react-quill/dist/quill.snow.css";
import sourceIcon from "../../assets/images/source code icon.png";
import "./BuildingMarketingEditor.scss";

const BuildingMarketingEditor = ({
  viewOnlyMode,
  tooltip,
  label,
  templateName,
  form,
  templateList,
  parentId,
  tab,
  fieldName,
  auditPanelDrawerVisible,
  useRichTextEditor = true,
  ...props
}) => {
  const { t } = useTranslation();
  const [templateId, setTemplateId] = useState(null);
  const { language, languageStatus } = useLanguage();
  const currentLanguage = language.current;
  const { lookupList } = useSelector(state => state.helperText);
  const [modalVisible, setModalVisibility] = useState(false);
  const fieldsType = FORM_FIELDS_TYPE.regular;
  const userRoles = useSelector(state => _get(state, "user.value.idTokenClaims.roles", []));
  const canViewSource = permissionsUtil.hasRole(USER_ROLES.ADMIN, userRoles);
  const [description, setDescription] = useState("");
  const [showHtml, setShowHtml] = useState(false);
  const [htmlError, setHtmlError] = useState("");
  const [pendingTemplateId, setPendingTemplateId] = useState(null);
  const [beautifiedHtml, setBeautifiedHtml] = useState("");

  // ReactQuill ref so we can paste HTML back into the editor safely
  const quillRef = useRef(null);
  const lastSelectionRef = useRef(null);

  const buildDescriptionFormValue = useCallback(
    marketingDescription =>
      languageStatus !== "primary"
        ? {
            translations: {
              [currentLanguage]: {
                descriptionTab: { marketingDescription },
              },
            },
          }
        : {
            descriptionTab: { marketingDescription },
          },
    [currentLanguage, languageStatus],
  );

  const syncDescriptionToForm = useCallback(
    value => {
      const sanitized = DOMPurify.sanitize(value, {
        ALLOWED_ATTR: ["href", "target", "rel"],
        // eslint-disable-next-line no-useless-escape
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
      });

      if (sanitized !== value) {
        setHtmlError(
          t("Only basic HTML tags (bold, italic, lists, links, line breaks, super/sub script) are allowed."),
        );
      } else {
        setHtmlError("");
      }

      form.setFieldsValue(buildDescriptionFormValue(sanitized));
      return sanitized;
    },
    [buildDescriptionFormValue, form, t],
  );

  const debouncedSyncDescriptionToForm = useMemo(
    () => debounce(value => syncDescriptionToForm(value), 250),
    [syncDescriptionToForm],
  );

  useEffect(
    () => () => {
      debouncedSyncDescriptionToForm.cancel();
    },
    [debouncedSyncDescriptionToForm],
  );

  useEffect(() => {
    if (!canViewSource && showHtml) {
      setShowHtml(false);
    }
  }, [canViewSource, showHtml]);

  const handleSelectionChange = useCallback(range => {
    if (range) {
      lastSelectionRef.current = range;
    }
  }, []);

  const handleLineBreak = useCallback(() => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection() || lastSelectionRef.current;
    if (!range) return;
    const lastEditableIndex = Math.max(editor.getLength() - 1, 0);
    const insertIndex = Math.min(range.index + range.length, lastEditableIndex);

    editor.focus();
    editor.insertText(insertIndex, "\n", "user");
    setTimeout(() => {
      const nextIndex = insertIndex + 1;
      editor.setSelection(nextIndex, 0, "user");
      lastSelectionRef.current = { index: nextIndex, length: 0 };
      editor.focus();
    }, 0);
  }, []);

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          ["bold", "italic"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link"],
          ["clean"],
          [{ script: "sub" }, { script: "super" }],
        ],
        handlers: {
          clean: handleLineBreak,
        },
      },
    }),
    [handleLineBreak],
  );

  useEffect(() => {
    if (showHtml) return undefined;

    const toolbar = quillRef.current?.getEditor?.()?.getModule("toolbar")?.container;
    if (!toolbar) return undefined;

    const handleMouseDown = event => {
      if (!event.target.closest(".ql-clean")) return;
      event.preventDefault();
    };

    toolbar.addEventListener("mousedown", handleMouseDown);

    return () => {
      toolbar.removeEventListener("mousedown", handleMouseDown);
    };
  }, [showHtml, quillModules]);

  useEffect(() => {
    const initializeDescription = async () => {
      const initialTemplateId = form.getFieldValue(["buildingInfo", "templateId"]);
      const initialLang = currentLanguage;
      const langStatus = languageStatus;
      setTemplateId(initialTemplateId);
      if (langStatus !== "primary") {
        const translatedDescription = _get(
          form.getFieldsValue(),
          ["translations", initialLang, "descriptionTab", "marketingDescription"],
          "",
        );
        if (translatedDescription) {
          setDescription(translatedDescription);
        } else if (initialTemplateId) {
          await populateDescription(initialTemplateId);
        }
      } else {
        const primaryDescription = form.getFieldValue(["descriptionTab", "marketingDescription"]);
        if (primaryDescription) {
          setDescription(primaryDescription);
        } else if (initialTemplateId) {
          await populateDescription(initialTemplateId);
        }
      }
    };

    initializeDescription();
  }, [form, templateList, currentLanguage, languageStatus]);

  useEffect(() => {
    const fetchDescriptionByLanguage = () => {
      const formValues = form.getFieldsValue();
      let translatedDescription;

      if (languageStatus !== "primary") {
        translatedDescription = _get(
          formValues,
          ["translations", currentLanguage, "descriptionTab", "marketingDescription"],
          "",
        );
      } else {
        translatedDescription = _get(formValues, ["descriptionTab", "marketingDescription"], "");
      }

      setDescription(current => (current === translatedDescription ? current : translatedDescription));
    };

    fetchDescriptionByLanguage();
  }, [currentLanguage, languageStatus, form]);

  useEffect(() => {
    if (templateId) {
      const country = form.getFieldValue(["buildingInfo", "address", "country"]);
      const selectedTemplate = templateList.find(item => item.id === templateId);
      if (!selectedTemplate) return;
      const shouldOverride = _isEmpty(description);
      if (shouldOverride) {
        const marketingDescription = _get(
          selectedTemplate,
          `translations.${
            country === "Mexico" && currentLanguage !== LANGUAGE_CODES.English.value
              ? LANGUAGE_CODES.Mexico.value
              : currentLanguage
          }.template`,
          selectedTemplate.template,
        );
        setDescription(marketingDescription);
      }
    }
  }, [templateId]);

  const fetchTemplateById = async id => {
    try {
      const response = await fetch(`/api/templates/${id}`);
      if (!response.ok) throw new Error("Failed to fetch");
      const data = await response.json();
      return data;
    } catch (err) {
      return null;
    }
  };
  const populateDescription = async selectedTemplateId => {
    if (!selectedTemplateId) return;
    const country = form.getFieldValue(["buildingInfo", "address", "country"]);
    const selectedTemplate = await fetchTemplateById(selectedTemplateId);
    if (!selectedTemplate) return;
    const marketingDescription = _get(
      selectedTemplate,
      `translations.${
        country === "Mexico" && currentLanguage !== LANGUAGE_CODES.English.value
          ? LANGUAGE_CODES.Mexico.value
          : currentLanguage
      }.template`,
      selectedTemplate.template,
    );
    const obj = {};
    if (languageStatus !== "primary") {
      obj.translations = {
        [currentLanguage]: { descriptionTab: { marketingDescription } },
      };
    } else {
      obj.descriptionTab = { marketingDescription };
    }
    form.setFieldsValue(obj);
    setDescription(marketingDescription);
    setTemplateId(selectedTemplateId);
  };

  const setTemplateDescription = newTemplateId => {
    if (_isEmpty(description)) {
      populateDescription(newTemplateId);
      setTemplateId(newTemplateId);
      form.setFieldsValue({
        buildingInfo: {
          ...form.getFieldValue("buildingInfo"),
          templateId: newTemplateId,
        },
      });
    } else {
      setPendingTemplateId(newTemplateId);
      setModalVisibility(true);
    }
  };

  const _onCancel = () => {
    setModalVisibility(false);

    const currentBuildingInfo = form.getFieldValue("buildingInfo") || {};
    form.setFieldsValue({ buildingInfo: { ...currentBuildingInfo, templateId } });
  };

  const _onConfirm = () => {
    if (pendingTemplateId) {
      const country = form.getFieldValue(["buildingInfo", "address", "country"]);
      const selectedTemplate = templateList.find(item => item.id === pendingTemplateId);
      if (!selectedTemplate) return;
      const marketingDescription = _get(
        selectedTemplate,
        `translations.${
          country === "Mexico" && currentLanguage !== LANGUAGE_CODES.English.value
            ? LANGUAGE_CODES.Mexico.value
            : currentLanguage
        }.template`,
        selectedTemplate.template,
      );
      const obj = {};
      if (languageStatus !== "primary") {
        obj.translations = {
          [currentLanguage]: { descriptionTab: { marketingDescription } },
        };
      } else {
        obj.descriptionTab = { marketingDescription };
      }
      if (!form) return;

      const currentBuildingInfo = form.getFieldValue("buildingInfo") || {};
      form.setFieldsValue({
        buildingInfo: { ...currentBuildingInfo, templateId: pendingTemplateId },
        ...obj,
      });
      setTemplateId(pendingTemplateId);
      setDescription(marketingDescription);
      setPendingTemplateId(null);
      setModalVisibility(false);
    }
  };

  const handleEditorChange = value => {
    setDescription(value);
    debouncedSyncDescriptionToForm(value);
  };

  const handleEditorBlur = () => {
    debouncedSyncDescriptionToForm.flush();
  };

  // When switching between source <-> rich, paste the HTML into Quill so Quill's internal
  // parser/Delta keeps sup/sub as formats. Relying on setting the value prop alone can
  // cause Quill to lose formats because it parses HTML differently.
  const handleSourceIconClick = () => {
    const enteringSource = !showHtml;

    if (enteringSource) {
      debouncedSyncDescriptionToForm.flush();
      let raw = (description || "").trim();

      if (raw && !raw.startsWith("<p") && !raw.endsWith("</p>")) {
        raw = `<p>${raw}</p>`;
      }

      const sanitised = DOMPurify.sanitize(raw, {
        ALLOWED_ATTR: ["href", "target", "rel"],
      });

      const beautified = beautify.html(sanitised, {
        indent_size: 2,
        wrap_line_length: 80,
      });

      setBeautifiedHtml(beautified);
    } else {
      // Switching back to rich editor. Sanitize the source and paste into Quill so
      // Quill correctly maps <sup>/<sub> to the 'script' format and preserves them.
      const updatedHtml = DOMPurify.sanitize(beautifiedHtml || "", {
        ALLOWED_ATTR: ["href", "target", "rel"],
      });

      if (quillRef.current && quillRef.current.getEditor) {
        const editor = quillRef.current.getEditor();
        // Use Quill clipboard to let Quill convert HTML -> Delta correctly
        editor.clipboard.dangerouslyPasteHTML(updatedHtml);
        // Keep local state in sync with editor's HTML
        const editorHtml = editor.root.innerHTML;
        setDescription(editorHtml);
        syncDescriptionToForm(editorHtml);
      } else {
        setDescription(updatedHtml);
        syncDescriptionToForm(updatedHtml);
      }
    }

    setShowHtml(enteringSource);
  };

  useEffect(() => {
    if (!showHtml) return;

    const raw = (description || "").trim();
    const wrapped = /^<p[^>]*>[\s\S]*<\/p>$/.test(raw) ? raw : `<p>${raw}</p>`;

    const sanitised = DOMPurify.sanitize(wrapped, {
      ALLOWED_ATTR: ["href", "target", "rel"],
    });

    const beautified = beautify.html(sanitised, {
      indent_size: 2,
      wrap_line_length: 80,
    });

    setBeautifiedHtml(beautified);
  }, [currentLanguage, templateId]);

  return (
    <>
      <Col xs={24} lg={24} className="building-marketing-description-col">
        <CustomFormItem
          className="form-item-label-with-drop-down building-marketing-description-item"
          label={
            <LabelWithDropDown
              label={t("Property.MarketingDescription")}
              name={["buildingInfo", "templateId"]}
              value={templateId}
              onChange={val => setTemplateDescription(val)}
              options={templateList.map(template => ({ label: template.name, value: template.id }))}
              tooltip={lookupList[HELPER_TEXT_KEYS.building.marketingDescription]}
              templateId={templateId}
              changeHandler={val => {
                form.setFieldsValue({ buildingInfo: { templateId: val } });
                setTemplateDescription(val);
              }}
              form={form}
              tab={BUILDING_TABS_KEYS[7]}
              fieldName={FORM_FIELDS_LOOKUP.building.buildingInfo.marketingDescription.key}
              auditPanelDrawerVisible={auditPanelDrawerVisible}
            />
          }
          name={["descriptionTab", "marketingDescription"]}
          rules={[
            {
              required: BUILDING_REQUIRED_FIELDS_LOOKUP[fieldsType].descriptionTab.marketingDescription,
              message: t("BuildingInfo.MarketingDescriptionRulesMessage"),
            },
            { whitespace: true, message: t("BuildingInfo.MarketingDescriptionWhitespaceMessage") },
            {
              validator: (_, value) => validateMarketingDescription(value, t("Property.MarketingDescription")),
            },
          ]}
        >
          <div className="building-marketing-editor">
            {canViewSource && (
              <Button
                className="building-marketing-source-button"
                type="text"
                icon={<img src={sourceIcon} alt="Source" />}
                onClick={handleSourceIconClick}
              />
            )}

            {showHtml && canViewSource ? (
              <div className="building-marketing-source-editor">
                <div className="building-marketing-source-toolbar" />
                <Input.TextArea
                  rows={10}
                  value={beautifiedHtml}
                  onChange={e => setBeautifiedHtml(e.target.value)}
                  placeholder="HTML Source"
                />
              </div>
            ) : (
              <ReactQuill
                ref={quillRef}
                theme="snow"
                key={templateId}
                value={description || ""}
                onChange={handleEditorChange}
                onBlur={handleEditorBlur}
                onChangeSelection={handleSelectionChange}
                modules={quillModules}
                formats={["bold", "italic", "list", "bullet", "link", "script"]}
                placeholder="Marketing Description"
              />
            )}
            {htmlError && <div className="error-msg">{htmlError}</div>}
          </div>
        </CustomFormItem>
      </Col>
      <AppModal
        centered
        wrapClassName="modal-associated-properties"
        title={t("Template.Confirmation")}
        width={600}
        minHeight="10vh"
        maxHeight="60vh"
        overFlowY="auto"
        visible={modalVisible}
        onCancel={_onCancel}
        footer={
          <Row justify="center" gutter={24}>
            <Col xs={12} sm={12} md={8} lg={8}>
              <Button block onClick={_onCancel}>
                {t("Buttons.Cancel")}
              </Button>
            </Col>
            <Col xs={12} sm={12} md={8} lg={8}>
              <Button block type="primary" onClick={_onConfirm}>
                {t("Buttons.Confirm")}
              </Button>
            </Col>
          </Row>
        }
        destroyOnClose
      >
        <p>{t("Template.ConfirmationMessage")}</p>
      </AppModal>
    </>
  );
};

BuildingMarketingEditor.propTypes = {
  viewOnlyMode: PropTypes.bool,
  tooltip: PropTypes.object,
  label: PropTypes.string,
  parentId: PropTypes.string,
  templateName: PropTypes.array,
  name: PropTypes.array,
  templateList: PropTypes.array.isRequired,
  form: PropTypes.object.isRequired,
  tab: PropTypes.string,
  fieldName: PropTypes.string,
  auditPanelDrawerVisible: PropTypes.bool,
  useRichTextEditor: PropTypes.bool,
};

export default BuildingMarketingEditor;
